import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useRef,
} from "react";
import {
  BasicInformation,
  ContentForEdit,
  ContentOperation,
  ErrorProp,
  Filter,
  Subtitle,
} from "../utils/typeConstants";
import { AVAILABLE_BITRATES } from "../utils/TriggerConstants";
import { initializeBitrateCheckState } from "../utils";
import { Dayjs } from "dayjs";

export type CheckboxState = {
  [key in keyof typeof AVAILABLE_BITRATES]: boolean;
};

interface Tags {
  filterTagData?: any[];
  displayTagData?: any[];
  keywordData?: any[];
}
interface DetailMeta {
  genresId: any[];
  cast: any[];
  parentalRating: number;
  partners: number[];
  productionHouse: number[];
  studio: number;
  releaseType?: string;
  releaseDate: string;
  ottReleaseDate: string;
  tvReleaseDate: string;
  budget: string;
  revenue: string;
}
interface RatingData {
  providerName?: string;
  providerDisplayName?: string;
  rating?: number;
  ratingLimit?: number;
}
interface PreviewContent {
  type?: string;
  mimetypeID?: number;
  carousel_type?: string;
  imageID?: number;
  videoURLID?: number;
  mediaType?: string;
}
export interface VideoData {
  typeId: string;
  profileID: string;
  licenceUrl: string;
  contentUrl: string;
  protocolID: string;
  reuseUrl: string;
  encryptionTypeID: string;
  qualityGroupId: string;
  qualityAdID: string | undefined;
  duration: string | number;
  runtime: string | number;
  drm_enabled: boolean;
}

/**Note: Temporary type, need to modify later */
interface previewVideoData {
  typeId: string | number;
  profileID: string | number;
  contentUrl: string;
  protocolID: string | number;
  encryptionTypeID: string | number;
  qualityGroupId: string | number;
  drm_enabled?: boolean;
  qualityAdID: number;
}

export interface VideoTrack {
  resolution: string;
  bitrate: string;
  codec: string;
  framerate: string;
}

export interface AudioTrack {
  bitrate: string;
  codec: string;
}

interface ContentContextProps {
  basicInformation: BasicInformation;
  setBasicInformation: React.Dispatch<React.SetStateAction<BasicInformation>>;
  detailMeta: DetailMeta;
  setDetailMeta: React.Dispatch<React.SetStateAction<DetailMeta>>;
  ratingData: RatingData;
  setRatingData: React.Dispatch<React.SetStateAction<RatingData>>;
  previewContent: PreviewContent;
  setPreviewContent: React.Dispatch<React.SetStateAction<PreviewContent>>;
  videoData: VideoData;
  setVideoData: React.Dispatch<React.SetStateAction<VideoData>>;
  previewVideoData: previewVideoData;
  setPreviewVideoData: React.Dispatch<React.SetStateAction<previewVideoData>>;
  selectedTags: number[];
  setSelectedTags: React.Dispatch<React.SetStateAction<number[]>>;
  setTag: React.Dispatch<React.SetStateAction<Tags>>;
  tags: Tags;
  setErrorContentMessage: React.Dispatch<React.SetStateAction<ErrorProp>>;
  errorContentMessage: ErrorProp;
  showLoader: boolean;
  setShowLoader: React.Dispatch<React.SetStateAction<boolean>>;
  source: string | undefined | null;
  setSource: React.Dispatch<React.SetStateAction<string | undefined | null>>;
  videoUrl: string;
  setVideoUrl: React.Dispatch<React.SetStateAction<string>>;
  playbackUrl: string;
  videoPresetIds: string[];
  selectedLangId: number[];
  uploadedData: any;
  setPlaybackURL: React.Dispatch<React.SetStateAction<string>>;
  setVideoPresetIds: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedLangId: React.Dispatch<React.SetStateAction<number[]>>;
  setUploadedData: any;
  fileData: any;
  setFileData: React.Dispatch<React.SetStateAction<any>>;
  inputRef: React.RefObject<HTMLInputElement>;
  isTranscoding: boolean;
  isUploading: boolean;
  setIsTranscoding: React.Dispatch<React.SetStateAction<boolean>>;
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
  playerDuration: number;
  playerCurrentTime: number;
  setPlayerDuration: React.Dispatch<React.SetStateAction<number>>;
  setPlayerCurrentTime: React.Dispatch<React.SetStateAction<number>>;
  playing: boolean;
  setPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  isUploaded: boolean;
  setIsUploaded: React.Dispatch<React.SetStateAction<boolean>>;
  isTranscodingCompleted: boolean;
  setEncodingCompleted: React.Dispatch<React.SetStateAction<boolean>>;
  progressPercentage: React.MutableRefObject<number>;
  pathName: React.MutableRefObject<string>;
  playerRef: React.MutableRefObject<any>;
  fileName: string;
  playerKey: number;
  setFileName: React.Dispatch<React.SetStateAction<string>>;
  setPlayerKey: React.Dispatch<React.SetStateAction<number>>;
  setIsSocialPublished: React.Dispatch<React.SetStateAction<boolean>>;
  isSocialPublished: boolean;
  setIsSaveState: React.Dispatch<React.SetStateAction<boolean>>;
  isSaveState: boolean;
  setContentForEdit: React.Dispatch<React.SetStateAction<ContentForEdit>>;
  initialEditContent: ContentForEdit;
  setInitialEditContent: React.Dispatch<React.SetStateAction<ContentForEdit>>;

  contentForEdit: ContentForEdit;
  setContentOperation: React.Dispatch<React.SetStateAction<ContentOperation>>;
  contentOperation: ContentOperation;
  setEditContentId: React.Dispatch<React.SetStateAction<string>>;
  editContentId: string;
  bitRateCheckState: CheckboxState;
  setBitrateCheckState: React.Dispatch<React.SetStateAction<CheckboxState>>;
  selectedReleaseDate: Dayjs | null;
  setSelectedReleaseDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
  selectedOttDate: Dayjs | null;
  setSelectedOttDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
  selectedTvDate: Dayjs | null;
  setSelectedTvDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
  setIsEditContent: React.Dispatch<React.SetStateAction<boolean>>;
  isEditContent: boolean;
  /**OTT STATUS RELATED STATES */
  contentIdList: string;
  setContentIdList: React.Dispatch<React.SetStateAction<string>>;
  /**OTT TRANSCODE VIDEO RELATED STATES */
  showRemoveVideoDialog: boolean;
  setShowRemoveVideoDialog: React.Dispatch<React.SetStateAction<boolean>>;
  isJobFailed: boolean;
  setIsJobFailed: React.Dispatch<React.SetStateAction<boolean>>;
  isTranscodingJobs: boolean;
  setIsTranscodingJobs: React.Dispatch<React.SetStateAction<boolean>>;
  isJobsExist: boolean;
  setIsJobsExist: React.Dispatch<React.SetStateAction<boolean>>;
  initialEditFilters: Filter[];
  setInitialEditFilters: React.Dispatch<React.SetStateAction<Filter[]>>;
  subtitleIds: number[];
  setSubtitleIds: React.Dispatch<React.SetStateAction<number[]>>;
  subtitlesData: Subtitle[];
  setSubtitlesData: React.Dispatch<React.SetStateAction<Subtitle[]>>;
  setSubtitleSrtData: React.Dispatch<React.SetStateAction<string>>;
  subtitleSrtData: string;
}

const ContentContext = createContext<ContentContextProps | undefined>(
  undefined
);

export const ContentProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [basicInformation, setBasicInformation] = useState<BasicInformation>({
    title: "",
    tagline: "",
    description: "",
    OverView: "",
    isFree: false,
    language: 0,
    contentType: 0,
    subContentType: 0,
    publishedType: 0,
    genre: [],
    parentalRating: 0,
  });
  const [errorContentMessage, setErrorContentMessage] = useState<ErrorProp>({
    title: "",
    description: "",
    flag: false,
  });

  const [isSaveState, setIsSaveState] = useState(false);

  const [detailMeta, setDetailMeta] = useState<DetailMeta>({
    genresId: [],
    cast: [],
    parentalRating: 0,
    partners: [],
    productionHouse: [],
    studio: 0,
    releaseType: "tv release",
    releaseDate: "",
    ottReleaseDate: "",
    tvReleaseDate: "",
    budget: "",
    revenue: "",
  });
  const [ratingData, setRatingData] = useState<RatingData>({
    providerName: "",
    providerDisplayName: "",
    rating: 0,
    ratingLimit: 0,
  });
  const [previewContent, setPreviewContent] = useState<PreviewContent>({
    type: "",
    mimetypeID: 0,
    carousel_type: "",
    imageID: 0,
    videoURLID: 0,
    mediaType: "image",
  });
  const [videoData, setVideoData] = useState<VideoData>({
    typeId: "",
    profileID: "",
    licenceUrl: "",
    contentUrl: "",
    protocolID: "",
    encryptionTypeID: "",
    qualityGroupId: "",
    qualityAdID: "",
    duration: "",
    runtime: "",
    reuseUrl: "",
    drm_enabled: false,
  });
  const [previewVideoData, setPreviewVideoData] = useState<previewVideoData>({
    typeId: 1,
    profileID: 1,
    contentUrl: "",
    protocolID: 1,
    encryptionTypeID: 1,
    qualityGroupId: 2,
    drm_enabled: false,
    qualityAdID: 1,
  });
  const [tags, setTag] = useState<Tags>({
    filterTagData: [],
    displayTagData: [],
    keywordData: [],
  });
  const [selectedTags, setSelectedTags] = useState<number[]>([]);

  const defaultEditOptions = {
    id: 0,
    title: "",
    tagline: "",
    description: "",
    OverView: "",
    duration: 0,
    runtime: "",
    isFree: false,
    contentType: {},
    subContentType: {},
    publishedType: "",
    publishedDate: "",
    language: "",
    parentalRating: {},
    rating: [],
    images: [],
    releaseStatus: {},
    genre: [],
    productionHouse: [],
    studio: {},
    displayTags: [],
    filterTags: [],
    keyword: [],
    deeplink: {},
    partners: [],
    customParameters: [],
    seriesMeta: {},
    preview: [],
    cast: [],
    altmeta: [],
    country: {},
    state: -1,
    video: [],
  };

  const [initialEditContent, setInitialEditContent] =
    useState<ContentForEdit>(defaultEditOptions);

  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [contentOperation, setContentOperation] = useState<ContentOperation>({
    title: false,
    tagline: false,
    description: false,
    OverView: false,
    isFree: false,
    language: false,
    contentType: false,
    subContentType: false,
    publishedType: false,
    genre: false,
    parentalRating: false,
    displayTags: false,
    filterTags: false,
    keywords: false,
    cast: false,
    productionHouse: false,
    partner: false,
    customParameters: false,
    altMeta: false,
    preview: false,
    video: false,
    subtitle: false,
    image: false,
    rating: false,
  });
  const [editContentId, setEditContentId] = useState("");
  const [isSocialPublished, setIsSocialPublished] = useState<boolean>(true);
  const [contentForEdit, setContentForEdit] =
    useState<ContentForEdit>(defaultEditOptions);

  const [source, setSource] = useState<string | undefined | null>();

  const [videoUrl, setVideoUrl] = useState<string>("");
  const [playbackUrl, setPlaybackURL] = useState<string>("");
  const [fileData, setFileData] = useState<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isTranscoding, setIsTranscoding] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [playerDuration, setPlayerDuration] = useState(0);
  const [playerCurrentTime, setPlayerCurrentTime] = useState(0);
  const [showRemoveVideoDialog, setShowRemoveVideoDialog] =
    useState<boolean>(false);

  const [playing, setPlaying] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [isTranscodingCompleted, setEncodingCompleted] =
    useState<boolean>(false);
  const progressPercentage = useRef<number>(30);
  const pathName = useRef<string>("");
  const playerRef = useRef<any>(null);
  const [fileName, setFileName] = useState<string>("");
  const [playerKey, setPlayerKey] = useState<number>(Date.now());
  const [bitRateCheckState, setBitrateCheckState] = useState<CheckboxState>(
    initializeBitrateCheckState()
  );

  //subtitle changes
  const [selectedLangId, setSelectedLangId] = useState<number[]>([]);

  /** Defining states to store the selected dates */
  const [selectedReleaseDate, setSelectedReleaseDate] = useState<Dayjs | null>(
    null
  );
  const [selectedOttDate, setSelectedOttDate] = useState<Dayjs | null>(null);
  const [selectedTvDate, setSelectedTvDate] = useState<Dayjs | null>(null);
  const [videoPresetIds, setVideoPresetIds] = useState<string[]>([]);
  const [uploadedData, setUploadedData] = useState<string[]>([]);
  const [isEditContent, setIsEditContent] = useState<boolean>(false);
  const [isTranscodingJobs, setIsTranscodingJobs] = useState<boolean>(false);

  /**OTT RELATED STATES */
  const [contentIdList, setContentIdList] = useState<string>("");
  const [isJobFailed, setIsJobFailed] = useState<boolean>(false);
  const [isJobsExist, setIsJobsExist] = useState<boolean>(false);
  const [initialEditFilters, setInitialEditFilters] = useState<Filter[]>([
    { category: "", value: "", operator: "", fromYear: null, toYear: null },
  ]);

  const [subtitleIds, setSubtitleIds] = useState<number[]>([]);
  const [subtitlesData, setSubtitlesData] = useState<Subtitle[]>([]);
  const [subtitleSrtData, setSubtitleSrtData] = useState("");

  return (
    <ContentContext.Provider
      value={{
        basicInformation,
        setBasicInformation,
        detailMeta,
        setDetailMeta,
        ratingData,
        setRatingData,
        previewContent,
        setPreviewContent,
        videoData,
        setVideoData,
        selectedTags,
        setSelectedTags,
        setTag,
        tags,
        setErrorContentMessage,
        errorContentMessage,
        showLoader,
        setShowLoader,
        previewVideoData,
        setPreviewVideoData,
        source,
        setSource,
        videoUrl,
        setVideoUrl,
        playbackUrl,
        setPlaybackURL,
        fileData,
        setFileData,
        inputRef,
        isTranscoding,
        setIsTranscoding,
        isUploading,
        setIsUploading,
        playerDuration,
        setPlayerDuration,
        playerCurrentTime,
        setPlayerCurrentTime,
        playing,
        setPlaying,
        isUploaded,
        setIsUploaded,
        isTranscodingCompleted,
        setEncodingCompleted,
        progressPercentage,
        pathName,
        playerRef,
        fileName,
        setFileName,
        playerKey,
        setPlayerKey,
        setIsSocialPublished,
        isSocialPublished,
        setContentForEdit,
        contentForEdit,
        setContentOperation,
        contentOperation,
        setEditContentId,
        editContentId,
        bitRateCheckState,
        setBitrateCheckState,
        selectedReleaseDate,
        setSelectedReleaseDate,
        selectedOttDate,
        setSelectedOttDate,
        selectedTvDate,
        setSelectedTvDate,
        setVideoPresetIds,
        videoPresetIds,
        selectedLangId,
        setSelectedLangId,
        setUploadedData,
        uploadedData,
        isEditContent,
        setIsEditContent,
        contentIdList,
        setContentIdList,
        initialEditContent,
        setInitialEditContent,
        /** OTT ContentUpload functionality states */
        isSaveState,
        setIsSaveState,
        showRemoveVideoDialog,
        setShowRemoveVideoDialog,
        isTranscodingJobs,
        setIsTranscodingJobs,
        isJobFailed,
        setIsJobFailed,
        isJobsExist,
        setIsJobsExist,
        initialEditFilters,
        setInitialEditFilters,
        subtitleIds,
        setSubtitleIds,
        subtitlesData,
        setSubtitlesData,
        subtitleSrtData,
        setSubtitleSrtData,
      }}
    >
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = (): ContentContextProps => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error("useContent must be used within a ContentProvider");
  }

  return context;
};
